import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Plus, X, Save, Trash2, AlertCircle, CheckCircle,
  Package, Gem, User, Truck, Store, Calendar, RefreshCw, DollarSign
} from 'lucide-react';
import api, { API_URL } from '../../services/api';
import ActionsPiece from './ActionsPiece';

const DepotDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [depot, setDepot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modal ajouter bijou
  const [showAddModal, setShowAddModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [bijouForm, setBijouForm] = useState({
    nom: '', categorie: 'bague', couleur_or: 'JAUNE', matiere: 'or',
    poids: '', poids_metal_net: '', dimensions: '',
    description: '', image_url: '',
    prix_vente: '', reference_article: '',
    modalite_remuneration: 'PRIX_NET', prix_net_deposant: '', commission_pct: '',
    // Chantier 3 — Acquisition
    prix_achat_total: '', prix_or_negocie_par_gramme: '',
    prix_pierres_total: '', prix_facon: '',
    fournisseur_id: '', collection_nom: '', marque: '',
    pierres: '', boutique_id: '',
  });

  // Modal reglement
  const [reglementBijou, setReglementBijou] = useState(null);
  const [reglementForm, setReglementForm] = useState({ montant: '', mode_paiement: 'especes', date_paiement: new Date().toISOString().split('T')[0], reference: '' });

  // Recherche dans les bijoux du depot (12/06/2026 — ref xlsm DEP-26-xxx cherchable)
  const [rechercheBijou, setRechercheBijou] = useState('');

  // Listes pour <ActionsPiece> (dropdown reaffecter + select vendre) — 12/06/2026
  const [depotsList, setDepotsList] = useState([]);
  const [clientsList, setClientsList] = useState([]);

  const fmt = (m) => parseFloat(m||0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' DH';
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '-';

  const loadDepot = async () => {
    setLoading(true);
    try {
      const r = await api.get(`${API_URL}/depots/${id}`);
      setDepot(r.data);
    } catch (err) { setError(err.response?.data?.error || 'Erreur chargement'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadDepot(); /* eslint-disable-next-line */ }, [id]);

  // Charge une fois les listes pour ActionsPiece (reaffecter / vendre)
  useEffect(() => {
    api.get(`${API_URL}/depots`).then(r => setDepotsList(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get(`${API_URL}/clients`).then(r => setClientsList(Array.isArray(r.data) ? r.data : (r.data?.clients || []))).catch(() => {});
  }, []);

  // Refresh apres une action piece (detail depot) : message + rechargement
  const onActionDone = (msg) => { if (msg) setSuccess(msg); loadDepot(); };
  // Normalise une ligne bijou du detail vers la forme attendue par <ActionsPiece>
  const mapPiece = (b) => ({
    id: b.id, id_produit: b.id_produit, reference_origine: b.reference_origine, nom: b.nom,
    pvp: b.pvp_magasin != null ? b.pvp_magasin : b.prix_vente,
    prix_net_deposant: b.prix_net_deposant, inventaire_actif: b.inventaire_actif,
    depot_id: depot?.id, numero_depot: depot?.numero_depot,
    deposant: depot?.deposant_label || depot?.fournisseur_nom || depot?.client_nom || depot?.deposant_nom || 'Déposant',
    montant_du_deposant: b.montant_du_deposant, montant_verse_deposant: b.montant_verse_deposant,
  });

  const handleAddBijou = async () => {
    if (!bijouForm.nom.trim()) { setError('Nom requis'); return; }
    const pv = parseFloat(bijouForm.prix_vente);
    if (!bijouForm.prix_vente || isNaN(pv) || pv <= 0) { setError('Prix de vente requis'); return; }

    if (depot.modalite === 'DEPOT_VENTE') {
      if (bijouForm.modalite_remuneration === 'PRIX_NET') {
        const pnd = parseFloat(bijouForm.prix_net_deposant);
        if (!bijouForm.prix_net_deposant || isNaN(pnd) || pnd <= 0) {
          setError('Prix net deposant requis'); return;
        }
        if (parseFloat(bijouForm.prix_net_deposant) > parseFloat(bijouForm.prix_vente)) {
          setError('Prix net ne peut pas etre superieur au prix de vente'); return;
        }
      } else {
        if (!bijouForm.commission_pct || parseFloat(bijouForm.commission_pct) < 0) {
          setError('Commission % requise'); return;
        }
      }
    }

    // Chantier 3 — validation prix or negocie pour ACHAT_DIRECT (sinon stock or boutique pas trace)
    if (depot.modalite === 'ACHAT_DIRECT') {
      const pog = parseFloat(bijouForm.prix_or_negocie_par_gramme);
      if (bijouForm.prix_or_negocie_par_gramme && (isNaN(pog) || pog < 0)) {
        setError('Prix or negocie invalide'); return;
      }
    }

    setSubmitting(true); setError('');
    try {
      const f = (v) => v === '' || v === null || v === undefined ? null : parseFloat(v);
      await api.post(`${API_URL}/depots/${id}/bijoux`, {
        nom: bijouForm.nom.trim(),
        categorie: bijouForm.categorie,
        couleur_or: bijouForm.couleur_or,
        matiere: bijouForm.matiere,
        poids: f(bijouForm.poids),
        poids_metal_net: f(bijouForm.poids_metal_net),
        dimensions: bijouForm.dimensions || null,
        description: bijouForm.description,
        image_url: bijouForm.image_url || null,
        prix_vente: parseFloat(bijouForm.prix_vente),
        reference_article: bijouForm.reference_article || null,
        modalite_remuneration: depot.modalite === 'DEPOT_VENTE' ? bijouForm.modalite_remuneration : null,
        prix_net_deposant: bijouForm.modalite_remuneration === 'PRIX_NET' && depot.modalite === 'DEPOT_VENTE' ? parseFloat(bijouForm.prix_net_deposant) : null,
        commission_pct: bijouForm.modalite_remuneration === 'COMMISSION_PCT' && depot.modalite === 'DEPOT_VENTE' ? parseFloat(bijouForm.commission_pct) : null,
        // Chantier 3 — Acquisition
        prix_achat_total: f(bijouForm.prix_achat_total),
        prix_or_negocie_par_gramme: f(bijouForm.prix_or_negocie_par_gramme),
        prix_pierres_total: f(bijouForm.prix_pierres_total),
        prix_facon: f(bijouForm.prix_facon),
        fournisseur_id: bijouForm.fournisseur_id ? parseInt(bijouForm.fournisseur_id, 10) : null,
        collection_nom: bijouForm.collection_nom || null,
        marque: bijouForm.marque || null,
        pierres: bijouForm.pierres || null,
        boutique_id: bijouForm.boutique_id ? parseInt(bijouForm.boutique_id, 10) : null,
      });
      setSuccess('Bijou ajoute');
      setShowAddModal(false);
      setBijouForm({
        nom: '', categorie: 'bague', couleur_or: 'JAUNE', matiere: 'or',
        poids: '', poids_metal_net: '', dimensions: '',
        description: '', image_url: '',
        prix_vente: '', reference_article: '',
        modalite_remuneration: 'PRIX_NET', prix_net_deposant: '', commission_pct: '',
        prix_achat_total: '', prix_or_negocie_par_gramme: '',
        prix_pierres_total: '', prix_facon: '',
        fournisseur_id: '', collection_nom: '', marque: '',
        pierres: '', boutique_id: '',
      });
      await loadDepot();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.response?.data?.error || 'Erreur ajout'); }
    finally { setSubmitting(false); }
  };

  const handleDeleteBijou = async (bijouId) => {
    if (!window.confirm('Retirer ce bijou du depot ?')) return;
    try {
      await api.delete(`${API_URL}/depots/${id}/bijoux/${bijouId}`);
      setSuccess('Bijou retire');
      await loadDepot();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.response?.data?.error || 'Erreur'); }
  };

  const ouvrirReglement = (bijou) => {
    const reste = parseFloat(bijou.montant_du_deposant || 0) - parseFloat(bijou.montant_verse_deposant || 0);
    setReglementBijou(bijou);
    setReglementForm({ montant: reste, mode_paiement: 'especes', date_paiement: new Date().toISOString().split('T')[0], reference: '' });
  };

  const handleReglement = async () => {
    if (!reglementBijou) return;
    if (!reglementForm.montant || parseFloat(reglementForm.montant) <= 0) { setError('Montant requis'); return; }
    setSubmitting(true);
    try {
      await api.post(`${API_URL}/depots/bijoux/${reglementBijou.id}/regler-deposant`, {
        montant: parseFloat(reglementForm.montant),
        mode_paiement: reglementForm.mode_paiement,
        date_paiement: reglementForm.date_paiement,
        reference: reglementForm.reference,
      });
      setSuccess('Deposant regle');
      setReglementBijou(null);
      await loadDepot();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.response?.data?.error || 'Erreur'); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div></div>;
  if (!depot) return <div className="p-8 text-center text-gray-500">Depot non trouve</div>;

  return (
    <div className="min-h-screen bg-gray-100 pb-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-700 to-pink-700 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex items-center gap-4 mb-2">
            <button onClick={() => navigate('/depots')} className="bg-white/20 hover:bg-white/30 p-2 rounded-lg"><ArrowLeft className="h-5 w-5" /></button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold">{depot.numero_depot}</h1>
              <p className="text-purple-200 text-sm">Depose le {fmtDate(depot.date_depot)}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={loadDepot} className="bg-white/20 hover:bg-white/30 p-2.5 rounded-lg"><RefreshCw className="h-4 w-4" /></button>
              {depot.statut === 'actif' && (
                <button onClick={() => setShowAddModal(true)} className="flex items-center gap-2 bg-white text-purple-700 px-4 py-2 rounded-lg font-medium hover:bg-purple-50">
                  <Plus className="h-4 w-4" /><span>Ajouter bijou</span>
                </button>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-3 mt-4">
            <div className="bg-white/10 rounded-lg px-3 py-2">
              <p className="text-xs text-purple-200">Deposant</p>
              <p className="font-medium flex items-center gap-1">
                {depot.deposant_type === 'client' && <User className="h-3 w-3" />}
                {depot.deposant_type === 'fournisseur' && <Truck className="h-3 w-3" />}
                {depot.deposant_label}
              </p>
            </div>
            <div className="bg-white/10 rounded-lg px-3 py-2">
              <p className="text-xs text-purple-200">Boutique</p>
              <p className="font-medium flex items-center gap-1"><Store className="h-3 w-3" />{depot.boutique_nom}</p>
            </div>
            <div className="bg-white/10 rounded-lg px-3 py-2">
              <p className="text-xs text-purple-200">Modalite</p>
              <p className="font-medium">{depot.modalite === 'DEPOT_VENTE' ? 'Depot-vente' : 'Achat direct'}</p>
            </div>
            <div className="bg-white/10 rounded-lg px-3 py-2">
              <p className="text-xs text-purple-200">Statut</p>
              <p className="font-medium uppercase">{depot.statut}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6 space-y-6">
        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center justify-between"><span className="flex items-center gap-2"><AlertCircle className="h-4 w-4" />{error}</span><button onClick={() => setError('')}><X className="h-4 w-4" /></button></div>}
        {success && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2"><CheckCircle className="h-4 w-4" />{success}</div>}

        {/* Si ACHAT_DIRECT - infos paiement */}
        {depot.modalite === 'ACHAT_DIRECT' && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <h3 className="text-sm font-bold text-green-800 mb-2">Paiement de l'achat</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div><p className="text-xs text-green-600">Montant total</p><p className="font-bold">{fmt(depot.montant_achat_total)}</p></div>
              <div><p className="text-xs text-green-600">Mode</p><p className="font-medium">{depot.mode_paiement_achat || '-'}</p></div>
              <div><p className="text-xs text-green-600">Date</p><p className="font-medium">{fmtDate(depot.date_paiement_achat)}</p></div>
              <div><p className="text-xs text-green-600">Reference</p><p className="font-medium">{depot.reference_paiement || '-'}</p></div>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="bg-white rounded-xl shadow-sm border p-4"><p className="text-xs text-gray-500">Bijoux</p><p className="text-2xl font-black text-purple-700">{depot.stats?.nb_bijoux || 0}</p></div>
          <div className="bg-white rounded-xl shadow-sm border p-4"><p className="text-xs text-gray-500">Vendus</p><p className="text-2xl font-black text-green-700">{depot.stats?.nb_vendus || 0}</p></div>
          <div className="bg-white rounded-xl shadow-sm border p-4"><p className="text-xs text-gray-500">A regler</p><p className="text-2xl font-black text-amber-700">{depot.stats?.nb_a_regler || 0}</p></div>
          <div className="bg-white rounded-xl shadow-sm border p-4"><p className="text-xs text-gray-500">Montant a regler</p><p className="text-lg font-black text-amber-700">{fmt(depot.stats?.montant_a_regler)}</p></div>
          <div className="bg-white rounded-xl shadow-sm border p-4"><p className="text-xs text-gray-500">Valeur stock</p><p className="text-lg font-black text-blue-700">{fmt(depot.stats?.valeur_stock)}</p></div>
        </div>

        {/* Notes */}
        {depot.notes && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-xs text-yellow-700 font-bold uppercase mb-1">Notes</p>
            <p className="text-sm text-gray-700">{depot.notes}</p>
          </div>
        )}

        {/* Liste bijoux */}
        <div className="bg-white rounded-xl shadow-sm border">
          <div className="px-5 py-4 border-b flex justify-between items-center">
            <h2 className="font-bold text-gray-800 flex items-center gap-2"><Gem className="h-5 w-5 text-purple-600" />BIJOUX DU DEPOT</h2>
            <div className="flex items-center gap-2">
              <input type="text" placeholder="Chercher : DEP-26-029, réf, nom…"
                value={rechercheBijou} onChange={e => setRechercheBijou(e.target.value)}
                className="px-3 py-1.5 border rounded-lg text-sm w-64" />
              {depot.statut === 'actif' && (
                <button onClick={() => setShowAddModal(true)} className="px-3 py-1 bg-purple-600 text-white rounded text-sm font-medium hover:bg-purple-700 flex items-center gap-1">
                  <Plus className="h-3 w-3" /><span>Ajouter</span>
                </button>
              )}
            </div>
          </div>

          {(depot.bijoux || []).length === 0 ? (
            <div className="text-center py-12">
              <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Aucun bijou dans ce depot</p>
              {depot.statut === 'actif' && (
                <button onClick={() => setShowAddModal(true)} className="mt-3 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700">Ajouter le premier bijou</button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs text-gray-500 uppercase">Réf. xlsm</th>
                    <th className="px-3 py-3 text-left text-xs text-gray-500 uppercase">Ref</th>
                    <th className="px-3 py-3 text-left text-xs text-gray-500 uppercase">Nom</th>
                    <th className="px-3 py-3 text-left text-xs text-gray-500 uppercase">Type</th>
                    <th className="px-3 py-3 text-right text-xs text-gray-500 uppercase">Prix vente</th>
                    {depot.modalite === 'DEPOT_VENTE' && <th className="px-3 py-3 text-right text-xs text-gray-500 uppercase">Du au deposant</th>}
                    <th className="px-3 py-3 text-center text-xs text-gray-500 uppercase">Statut</th>
                    <th className="px-3 py-3 text-center text-xs text-gray-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {depot.bijoux.filter(b => {
                    const q = rechercheBijou.trim().toLowerCase();
                    if (!q) return true;
                    return [b.reference_origine, b.id_produit, b.nom, b.description]
                      .some(v => (v || '').toLowerCase().includes(q));
                  }).map(b => {
                    const reste = parseFloat(b.montant_du_deposant || 0) - parseFloat(b.montant_verse_deposant || 0);
                    return (
                      <tr key={b.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 font-mono text-xs font-semibold text-purple-700">{b.reference_origine || '—'}</td>
                        <td className="px-3 py-2 font-mono text-xs">{b.id_produit}</td>
                        <td className="px-3 py-2">
                          <p className="font-medium text-gray-900">{b.nom}</p>
                          <div className="flex gap-1 mt-0.5">
                            {b.categorie && <span className="text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">{b.categorie}</span>}
                            {b.couleur_or && <span className="text-xs px-1.5 py-0.5 rounded bg-yellow-50 text-yellow-700">{b.couleur_or}</span>}
                            {b.poids && <span className="text-xs px-1.5 py-0.5 rounded bg-gray-50 text-gray-700">{b.poids}g</span>}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-600">
                          {b.modalite_remuneration === 'PRIX_NET' && `Prix net: ${fmt(b.prix_net_deposant)}`}
                          {b.modalite_remuneration === 'COMMISSION_PCT' && `Commission: ${b.commission_pct}%`}
                          {!b.modalite_remuneration && '-'}
                        </td>
                        <td className="px-3 py-2 text-right font-bold text-gray-900">{fmt(b.prix_vente)}</td>
                        {depot.modalite === 'DEPOT_VENTE' && (
                          <td className="px-3 py-2 text-right">
                            {b.statut_paiement_deposant !== 'non_applicable' ? (
                              <div>
                                <span className="font-bold">{fmt(b.montant_du_deposant)}</span>
                                {parseFloat(b.montant_verse_deposant) > 0 && parseFloat(b.montant_verse_deposant) < parseFloat(b.montant_du_deposant) && (
                                  <p className="text-xs text-amber-600">Reste: {fmt(reste)}</p>
                                )}
                              </div>
                            ) : <span className="text-gray-400">-</span>}
                          </td>
                        )}
                        <td className="px-3 py-2 text-center">
                          {b.inventaire_actif === 1 ? (
                            <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700 font-medium">En stock</span>
                          ) : b.statut_paiement_deposant === 'paye' ? (
                            <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 font-medium">Vendu - Regle</span>
                          ) : b.statut_paiement_deposant === 'en_attente' ? (
                            <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-700 font-medium">A regler</span>
                          ) : (
                            <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700 font-medium">Vendu</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {b.inventaire_actif === 1 ? (
                            <div className="flex items-center justify-center gap-2">
                              <ActionsPiece piece={mapPiece(b)} depots={depotsList} clients={clientsList} onDone={onActionDone} fmt={fmt} />
                              <button onClick={() => handleDeleteBijou(b.id)} className="text-red-500 hover:text-red-700" title="Retirer du depot">
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          ) : b.statut_paiement_deposant === 'en_attente' ? (
                            <button onClick={() => ouvrirReglement(b)} className="px-2 py-1 bg-amber-500 text-white rounded text-xs font-medium hover:bg-amber-600">Regler</button>
                          ) : <span className="text-gray-300 text-xs">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ===== MODAL AJOUTER BIJOU ===== */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-2xl max-h-[95vh] flex flex-col">
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-4 rounded-t-2xl flex justify-between items-center">
              <h3 className="text-lg font-semibold">Ajouter un bijou au depot</h3>
              <button onClick={() => setShowAddModal(false)} className="hover:bg-white/20 p-1 rounded"><X className="h-5 w-5" /></button>
            </div>
            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-3 mx-6 mt-3 rounded flex items-start gap-2">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Nom du bijou *</label>
                <input type="text" value={bijouForm.nom} onChange={e => setBijouForm({...bijouForm, nom: e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="Ex: Bague or jaune avec diamant" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Reference article</label>
                  <input type="text" value={bijouForm.reference_article} onChange={e => setBijouForm({...bijouForm, reference_article: e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="Optionnel" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Categorie</label>
                  <select value={bijouForm.categorie} onChange={e => setBijouForm({...bijouForm, categorie: e.target.value})} className="w-full px-3 py-2 border rounded-lg">
                    <option value="bague">Bague</option>
                    <option value="collier">Collier</option>
                    <option value="bracelet">Bracelet</option>
                    <option value="boucles_oreilles">Boucles d'oreilles</option>
                    <option value="pendentif">Pendentif</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Couleur or</label>
                  <select value={bijouForm.couleur_or} onChange={e => setBijouForm({...bijouForm, couleur_or: e.target.value})} className="w-full px-3 py-2 border rounded-lg">
                    <option value="JAUNE">Jaune</option>
                    <option value="BLANC">Blanc</option>
                    <option value="ROSE">Rose</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Matiere</label>
                  <select value={bijouForm.matiere} onChange={e => setBijouForm({...bijouForm, matiere: e.target.value})} className="w-full px-3 py-2 border rounded-lg">
                    <option value="or">Or</option>
                    <option value="argent">Argent</option>
                    <option value="diamant">Diamant</option>
                    <option value="plaque">Plaque</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Poids (g)</label>
                  <input type="text" inputMode="decimal" value={bijouForm.poids} onChange={e => setBijouForm({...bijouForm, poids: e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="Optionnel" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Poids metal net (g)</label>
                  <input type="text" inputMode="decimal" value={bijouForm.poids_metal_net} onChange={e => setBijouForm({...bijouForm, poids_metal_net: e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="Defaut = poids brut" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Dimensions</label>
                  <input type="text" value={bijouForm.dimensions} onChange={e => setBijouForm({...bijouForm, dimensions: e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="Optionnel" />
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-500 mb-1">Pierres (description libre)</label>
                <input type="text" value={bijouForm.pierres} onChange={e => setBijouForm({...bijouForm, pierres: e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="Ex: 5 diamants 0.05ct chacun" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Collection</label>
                  <input type="text" value={bijouForm.collection_nom} onChange={e => setBijouForm({...bijouForm, collection_nom: e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="Optionnel" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Marque</label>
                  <input type="text" value={bijouForm.marque} onChange={e => setBijouForm({...bijouForm, marque: e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="Optionnel" />
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-500 mb-1">Description</label>
                <textarea value={bijouForm.description} onChange={e => setBijouForm({...bijouForm, description: e.target.value})} className="w-full px-3 py-2 border rounded-lg" rows="2" placeholder="Description, etat, particularites..." />
              </div>

              <div>
                <label className="block text-xs text-gray-500 mb-1">Image URL</label>
                <input type="text" value={bijouForm.image_url} onChange={e => setBijouForm({...bijouForm, image_url: e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="https://... (upload futur)" />
              </div>

              <div>
                <label className="block text-xs text-gray-500 mb-1">Prix de vente (DH) *</label>
                <input type="text" inputMode="decimal" value={bijouForm.prix_vente} onChange={e => setBijouForm({...bijouForm, prix_vente: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-lg font-bold" placeholder="0.00" />
              </div>

              {depot.modalite === 'ACHAT_DIRECT' && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
                  <h4 className="text-sm font-bold text-amber-800">Acquisition (achat direct)</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Prix achat total (DH)</label>
                      <input type="text" inputMode="decimal" value={bijouForm.prix_achat_total} onChange={e => setBijouForm({...bijouForm, prix_achat_total: e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="0.00" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Prix or negocie (DH/g)</label>
                      <input type="text" inputMode="decimal" value={bijouForm.prix_or_negocie_par_gramme} onChange={e => setBijouForm({...bijouForm, prix_or_negocie_par_gramme: e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="0.00" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Prix pierres total (DH)</label>
                      <input type="text" inputMode="decimal" value={bijouForm.prix_pierres_total} onChange={e => setBijouForm({...bijouForm, prix_pierres_total: e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="0.00" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Prix facon (DH)</label>
                      <input type="text" inputMode="decimal" value={bijouForm.prix_facon} onChange={e => setBijouForm({...bijouForm, prix_facon: e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="0.00" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Fournisseur ID (optionnel)</label>
                    <input type="text" inputMode="numeric" value={bijouForm.fournisseur_id} onChange={e => setBijouForm({...bijouForm, fournisseur_id: e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="Optionnel" />
                  </div>
                  {bijouForm.poids_metal_net && bijouForm.prix_or_negocie_par_gramme && (
                    <p className="text-xs text-amber-700">
                      Valeur or boutique : <span className="font-bold">{fmt(parseFloat(bijouForm.poids_metal_net) * parseFloat(bijouForm.prix_or_negocie_par_gramme))}</span>
                      <span className="text-gray-500"> (sera tracee dans stock or boutique a la creation)</span>
                    </p>
                  )}
                  {/* V2.Y : règle métier centrale */}
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-xs text-blue-800">
                      <span className="font-bold">Stock or affecté :</span> Centrale
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      Le bijou entre en stock CENTRALE. Pour le transférer vers une boutique, utiliser le module Transferts.
                    </p>
                  </div>
                </div>
              )}

              {depot.modalite === 'DEPOT_VENTE' && (
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 space-y-3">
                  <h4 className="text-sm font-bold text-purple-800">Remuneration du deposant</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={() => setBijouForm({...bijouForm, modalite_remuneration: 'PRIX_NET'})} className={`p-2 rounded-lg border-2 text-center ${bijouForm.modalite_remuneration === 'PRIX_NET' ? 'bg-white border-purple-400' : 'bg-gray-50 border-gray-200'}`}>
                      <p className="text-sm font-bold">Prix net fixe</p>
                      <p className="text-xs text-gray-500">Montant convenu</p>
                    </button>
                    <button onClick={() => setBijouForm({...bijouForm, modalite_remuneration: 'COMMISSION_PCT'})} className={`p-2 rounded-lg border-2 text-center ${bijouForm.modalite_remuneration === 'COMMISSION_PCT' ? 'bg-white border-purple-400' : 'bg-gray-50 border-gray-200'}`}>
                      <p className="text-sm font-bold">Commission %</p>
                      <p className="text-xs text-gray-500">Notre marge en %</p>
                    </button>
                  </div>
                  {bijouForm.modalite_remuneration === 'PRIX_NET' && (
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Prix net deposant (DH) *</label>
                      <input type="text" inputMode="decimal" value={bijouForm.prix_net_deposant} onChange={e => setBijouForm({...bijouForm, prix_net_deposant: e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="0.00" />
                      {bijouForm.prix_vente && bijouForm.prix_net_deposant && (
                        <p className="text-xs text-gray-500 mt-1">Marge bijouterie: <span className="font-bold text-green-600">{fmt(parseFloat(bijouForm.prix_vente) - parseFloat(bijouForm.prix_net_deposant))}</span></p>
                      )}
                    </div>
                  )}
                  {bijouForm.modalite_remuneration === 'COMMISSION_PCT' && (
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Commission % *</label>
                      <input type="text" inputMode="decimal" value={bijouForm.commission_pct} onChange={e => setBijouForm({...bijouForm, commission_pct: e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="20" />
                      {bijouForm.prix_vente && bijouForm.commission_pct && (
                        <p className="text-xs text-gray-500 mt-1">
                          Du au deposant: <span className="font-bold">{fmt(parseFloat(bijouForm.prix_vente) * (1 - parseFloat(bijouForm.commission_pct)/100))}</span> -
                          Notre commission: <span className="font-bold text-green-600">{fmt(parseFloat(bijouForm.prix_vente) * parseFloat(bijouForm.commission_pct)/100)}</span>
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t bg-gray-50 rounded-b-2xl flex justify-end gap-3">
              <button onClick={() => setShowAddModal(false)} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg">Annuler</button>
              <button onClick={handleAddBijou} disabled={submitting} className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium disabled:opacity-50 flex items-center gap-2">
                <Save className="h-4 w-4" /><span>{submitting ? 'Ajout...' : 'Ajouter'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL REGLEMENT ===== */}
      {reglementBijou && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-md">
            <div className="bg-gradient-to-r from-amber-500 to-orange-600 text-white px-6 py-4 rounded-t-2xl flex justify-between items-center">
              <h3 className="text-lg font-semibold">Regler le deposant</h3>
              <button onClick={() => setReglementBijou(null)} className="hover:bg-white/20 p-1 rounded"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <p><span className="font-bold">Bijou:</span> {reglementBijou.nom} ({reglementBijou.id_produit})</p>
                <p><span className="font-bold">Du:</span> {fmt(reglementBijou.montant_du_deposant)}</p>
                <p><span className="font-bold">Deja verse:</span> {fmt(reglementBijou.montant_verse_deposant)}</p>
                <p><span className="font-bold">Reste:</span> <span className="text-amber-700 font-bold">{fmt((reglementBijou.montant_du_deposant||0) - (reglementBijou.montant_verse_deposant||0))}</span></p>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Montant a verser *</label>
                <input type="text" inputMode="decimal" value={reglementForm.montant} onChange={e => setReglementForm({...reglementForm, montant: e.target.value})} onFocus={e => e.target.select()} className="w-full px-3 py-2 border rounded-lg text-lg font-bold" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Mode</label>
                  <select value={reglementForm.mode_paiement} onChange={e => setReglementForm({...reglementForm, mode_paiement: e.target.value})} className="w-full px-3 py-2 border rounded-lg">
                    <option value="especes">Especes</option>
                    <option value="cheque">Cheque</option>
                    <option value="virement">Virement</option>
                    <option value="carte">Carte</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Date</label>
                  <input type="date" value={reglementForm.date_paiement} onChange={e => setReglementForm({...reglementForm, date_paiement: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Reference</label>
                <input type="text" value={reglementForm.reference} onChange={e => setReglementForm({...reglementForm, reference: e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="N° cheque..." />
              </div>
            </div>
            <div className="px-6 py-4 border-t bg-gray-50 rounded-b-2xl flex justify-end gap-3">
              <button onClick={() => setReglementBijou(null)} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg">Annuler</button>
              <button onClick={handleReglement} disabled={submitting} className="px-6 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-medium disabled:opacity-50 flex items-center gap-2">
                <DollarSign className="h-4 w-4" /><span>{submitting ? '...' : 'Confirmer'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepotDetail;
